/**
 * App-level context for triggering data refreshes.
 * 
 * Instead of URL navigation (website-like), this provides a refresh mechanism
 * that remounts components to fetch fresh data - more app-like behavior.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface AppContextValue {
    /** Increment to trigger component remount and data refetch */
    refreshKey: number;
    /** Call to trigger a refresh (e.g., when date changes) */
    triggerRefresh: () => void;
    /** Current local date (YYYY-MM-DD) */
    currentDate: string;
}

const AppContext = createContext<AppContextValue | null>(null);

/**
 * Get today's date in YYYY-MM-DD format (local time).
 */
function getLocalDate(): string {
    return new Date().toLocaleDateString('en-CA');
}

export function AppProvider({ children }: { children: ReactNode }) {
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentDate, setCurrentDate] = useState(getLocalDate());
    const lastKnownDate = useRef(getLocalDate());
    const lastRefreshTime = useRef(0);
    const location = useLocation();

    const triggerRefresh = useCallback(() => {
        const nextDate = getLocalDate();
        console.log('[app] Triggering refresh');
        lastKnownDate.current = nextDate;
        setCurrentDate(nextDate);
        setRefreshKey(k => k + 1);
    }, []);

    // Detect date changes on visibility/focus and while the app stays in foreground.
    useEffect(() => {
        const checkDateChange = () => {
            // Debounce: ignore if refreshed in last 500ms (prevents double-fire from visibility+focus)
            if (Date.now() - lastRefreshTime.current < 500) return;

            const newDate = getLocalDate();
            if (newDate !== lastKnownDate.current) {
                lastRefreshTime.current = Date.now();
                console.log(`[app] Date changed: ${lastKnownDate.current} → ${newDate}`);
                lastKnownDate.current = newDate;

                // Only trigger refresh on puzzle-related pages
                const puzzlePages = ['/daily', '/day/'];
                const isOnPuzzlePage = puzzlePages.some(p => location.pathname.startsWith(p) || location.pathname === p);

                if (isOnPuzzlePage) {
                    triggerRefresh();
                }
            }
        };

        // Check on visibility change (app resume)
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                checkDateChange();
            }
        };

        // Check on focus (window focus)
        const handleFocus = () => checkDateChange();

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleFocus);
        const intervalId = window.setInterval(checkDateChange, 30_000);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleFocus);
            window.clearInterval(intervalId);
        };
    }, [location.pathname, triggerRefresh]);

    return (
        <AppContext.Provider value={{ refreshKey, triggerRefresh, currentDate }}>
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within AppProvider');
    }
    return context;
}
